/**
 * Pre-built URL patterns for the cache-clear dropdown. Patterns are anchored
 * regexes evaluated by Varnish against the request path. Derived from public
 * routes in jade (`config/routes.rb`).
 *
 * `description` is shown to all editors and stays plain-English so it doesn't
 * read like code. `pattern` is the regex shipped to Varnish; it's exposed in
 * the UI only when the editor opts in to seeing it.
 */
export interface CacheQuickClear {
  id: string;
  label: string;
  description: string;
  /** Regex pattern shipped to Varnish via x-invalidate-pattern. */
  pattern: string;
  /**
   * When true, the chip renders with the primary-action treatment in the
   * dropdown. Reserved for the most-frequent clear (the homepage).
   */
  featured?: boolean;
}

export const CACHE_QUICK_CLEARS: CacheQuickClear[] = [
  {
    id: 'homepage',
    label: 'Homepage',
    description: 'Just the homepage',
    pattern: '^/$',
    featured: true,
  },
  {
    id: 'articles',
    label: 'All articles',
    description: 'Article pages plus topic and keyword listings',
    pattern: '^/articles(/|$)',
  },
  {
    id: 'topics',
    label: 'All topic pages',
    description: 'Topic landing pages across articles, authors, and pages',
    pattern: '^/(topics|articles/topic|authors/topic|pages/topic)(/|$)',
  },
  {
    id: 'blogs',
    label: 'All blog posts',
    description: 'Every blog post',
    pattern: '^/blogs(/|$)',
  },
  {
    id: 'events',
    label: 'All events',
    description: 'Every event page',
    pattern: '^/events(/|$)',
  },
  {
    id: 'products',
    label: 'All products',
    description: 'Product pages plus category and keyword listings',
    pattern: '^/products(/|$)',
  },
  {
    id: 'classifieds',
    label: 'All classifieds',
    description: 'Classified listings plus category pages',
    pattern: '^/classifieds(/|$)',
  },
  {
    id: 'directories',
    label: 'All directory listings',
    description: 'Every directory listing',
    pattern: '^/directories(/|$)',
  },
  {
    id: 'newsletters',
    label: 'All newsletter pages',
    description: 'Newsletter pages and issue archives',
    pattern: '^/(newsletters|newsletter-issues)(/|$)',
  },
];

export interface CachePatternExample {
  label: string;
  pattern: string;
}

/**
 * Generic regex shapes surfaced under the "Clear by URL Pattern" input.
 * Each example shows the syntax for a different match style — exact URL,
 * path prefix, alternation — so editors can adapt them to their target
 * path. The patterns match URL paths only; they do not follow content
 * tags, authors, or other relationships.
 */
export const CACHE_PATTERN_EXAMPLES: CachePatternExample[] = [
  {
    label: 'One exact URL',
    pattern: '^/section/page-slug$',
  },
  {
    label: 'Everything under a path',
    pattern: '^/section/',
  },
  {
    label: 'Two sections at once',
    pattern: '^/(section-one|section-two)/',
  },
  {
    label: 'All CSS and JavaScript files',
    pattern: '\\.(css|js)$',
  },
];
